import { useT } from "@repo/transifex";
import { type Currency } from "~/api/products/types";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ReportTableSection } from "~/components/report";
import { priceFormatter } from "~/utils/price-formatter";

type Row = { name: string; amount: number; productCount: number };

type SalesTableSectionProps = {
  title: string;
  nameLabel: string;
  data: { rows: Row[]; totals: Row; currency: Currency };
};

/** Shared table for the "Sales by category" and "Sales per user" sections. */
export const SalesTableSection = ({
  title,
  nameLabel,
  data
}: SalesTableSectionProps) => {
  const t = useT();
  const { locale } = useCurrentTillLocalisation();

  const fmt = (cents: number) =>
    priceFormatter({ value: cents, currency: data.currency, locale });

  const columns = [
    { key: "name", label: nameLabel, align: "left" as const, width: "w-1/2" },
    {
      key: "amount",
      label: t("Amount"),
      align: "right" as const,
      width: "w-1/4"
    },
    {
      key: "productCount",
      label: t("Number of products"),
      align: "right" as const,
      width: "w-1/4"
    }
  ];

  const rows = data.rows.map((row) => ({
    name: row.name,
    amount: fmt(row.amount),
    productCount: row.productCount
  }));

  const footer = {
    name: t("TOTAL"),
    amount: fmt(data.totals.amount),
    productCount: data.totals.productCount
  };

  return (
    <ReportTableSection
      title={title}
      columns={columns}
      rows={rows}
      footer={footer}
    />
  );
};
